﻿import React, { useState, useContext, useMemo } from 'react';
import Context, { context_type } from '@Util/Context';
import { useTranslation } from 'react-i18next';
import { str_isEmpty } from '@Util/Utils';
import { Customer } from '@Util/Types';

import Table_Head from '@Elements/Table/Table_Head';
import Table_Data from '@Elements/Table/Table_Data';
import Customer_Name_With_Color from '@Elements/Table/Customer_Name_With_Color';
import Edit_Customers_Popup from '@Modules/Popups/Edit_Customers_Popup';
import { Icon_Edit } from '@Elements/ImageElements/Icons';

export default function Customers_Table(): JSX.Element {
    const { customers }: context_type = useContext(Context);
    const [t] = useTranslation();

    const [sort_direction, set_sort_direction] = useState<"Ascending" | "Descending">("Ascending");
    const [active_sorting_column, set_active_sorting_column] = useState<string>("name");
    const [edit_customer_popup_active, set_edit_customer_popup_active] = useState<boolean>(false);
    const [edit_customer_id, set_edit_customer_id] = useState<number>(0);

    const table_data: Customer[] = useMemo(() => {
        let sorted_data: Customer[] = [];

        if (sort_direction == "Ascending") {
            sorted_data = customers.sort((a: Customer, b: Customer) => a[active_sorting_column] > b[active_sorting_column] ? 1 : -1);
        } else {
            sorted_data = customers.sort((a: Customer, b: Customer) => a[active_sorting_column] < b[active_sorting_column] ? 1 : -1);
        }

        return sorted_data;
    }, [customers, sort_direction, active_sorting_column]);

    function handleSetSortingOnTableHead(col: string): void {
        if (str_isEmpty(col)) {
            throw new Error("Handle sorting on table: Column is empty");
        }

        if (col === active_sorting_column) {
            if (sort_direction == "Ascending") {
                return set_sort_direction("Descending");
            } else {
                return set_sort_direction("Ascending");
            }
        }

        set_active_sorting_column(col);
        set_sort_direction("Ascending");
    }

    function Empty_Table(): JSX.Element {
        return (
            <tr>
                <Table_Data align="left">
                    <span className="Table_Data_Customer_Name">{t('Text.NoCustomersToShow')}</span>
                </Table_Data>
                <Table_Data align="right">
                    <></>
                </Table_Data>
            </tr>
        )
    }

    return (
        <>
            <div className="Table_Wrapper">
                <table className="Table">
                    <thead>
                        <tr>
                            <Table_Head
                                align="left"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Customer')}
                                sortingAction={() => handleSetSortingOnTableHead('name')}
                                sortingDataName="name"
                                style={{ width: "50%" }}
                            />
                            <Table_Head
                                align="center"
                                sortDirection={sort_direction}
                                activeSortingColumn={active_sorting_column}
                                text={t('Text.Project')}
                                sortingAction={() => handleSetSortingOnTableHead('projects')}
                                sortingDataName="projects"
                                style={{ width: "25%" }}
                            />
                            <Table_Head
                                align="right"
                                text={t('Text.Action')}
                                style={{ width: "25%" }}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {
                            table_data.isEmpty() ?
                                <Empty_Table /> :
                                table_data.map((customer: Customer, index: number) =>
                                    <tr key={`customer_table_row${index}`}>
                                        <Table_Data
                                            align="left"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "50%" }}
                                        >
                                            <Customer_Name_With_Color
                                                name={customer.name}
                                                color={customer.color}
                                            />
                                        </Table_Data>
                                        <Table_Data
                                            align="center"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "25%" }}
                                        >
                                            {`${customer.projects.length} ${t('Text.Pcs')}`}
                                        </Table_Data>
                                        <Table_Data
                                            align="right"
                                            className="Table_Data_With_Buttons"
                                            style={{ width: "25%" }}
                                        >
                                            <button
                                                type="button"
                                                className="Button_Icon"
                                                onClick={() => {
                                                    set_edit_customer_id(customer.id);
                                                    set_edit_customer_popup_active(true);
                                                }}
                                            >
                                                <Icon_Edit />
                                            </button>
                                        </Table_Data>
                                    </tr>
                                )
                        }
                    </tbody>
                </table>
            </div>
            <Edit_Customers_Popup
                open={edit_customer_popup_active}
                set_open={set_edit_customer_popup_active}
                chosen_customer_id={edit_customer_id}
            />
        </>
    );
}